import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Volume2, VolumeX, X, Calendar, MapPin, CheckCircle } from 'lucide-react';

/**
 * Echtzeit-Benachrichtigungssystem für Sales/Vertrieb Dashboard
 * Features:
 * - Polling alle 30 Sekunden für neue Touren/Termine
 * - Browser Push Notifications
 * - Sound-Benachrichtigung
 * - Visueller Badge mit Zähler
 */

const SalesNotificationSystem = ({ salespersonId, token }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const previousNotificationIds = useRef(new Set());
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Initialize audio element
  useEffect(() => {
    // Create audio element for notification sound
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQwAJ5XF6NmATwALf7Xp6qluCACAtubqnWwMAJi46NuFTgAAnLfj0XhEAAC0t9bAaz4AAMu0yK5gOQAA4K+8nlYxAADzqLCOTisAAAOiq4JGJgAAEZumeFwhAAAena6KYSkAAD6mtpmKRQAAaLS2p6VnAAB5ur+4vmkAAHq4v7q/ZwAAcLK4uLtkAABjqrC1s18AAFOiprCsWgAAS5qgrKVUAABFk5qnoU8AAEGOlaOdSwAAPouRoJlIAAA9h46emEYAADyEi5yWRQAAPYKJmpVEAAA+gIeYk0MAAD9+hZaRQgAAQHyCk49BAABBP4CRj0AAAEL9fo+NQAAAQ/t9jo1AAABE+XyMjEAABET4e4uLQAAERfd6ioqAAAVF9nmJiYAABkX1eIiIgAAHRfR3h4eAAAhE83aGhoAAQA==');
    audioRef.current.volume = 0.5;
    
    // Check browser notification permission
    if ('Notification' in window) {
      setPermissionGranted(Notification.permission === 'granted');
    }
  }, []);

  // Request notification permission
  const requestPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setPermissionGranted(permission === 'granted');
      return permission === 'granted';
    }
    return false;
  };

  // Play notification sound
  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body, icon = '📅') => {
    if (permissionGranted && 'Notification' in window) {
      new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'sales-notification',
        requireInteraction: false
      });
    }
  }, [permissionGranted]);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    if (!salespersonId) return;

    try {
      const response = await fetch(
        `${API_URL}/api/sales/notifications?salesperson_id=${salespersonId}`,
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newNotifications = data.notifications || [];
        
        // Check for new notifications
        const newIds = new Set(newNotifications.map(n => n.id));
        const previousIds = previousNotificationIds.current;
        
        // Find truly new notifications (not seen before)
        const brandNewNotifications = newNotifications.filter(n => 
          !previousIds.has(n.id) && !n.read
        );

        // If there are new notifications, alert the user
        if (brandNewNotifications.length > 0 && lastCheckTime !== null) {
          playSound();
          
          // Show browser notification for the first new one
          const latest = brandNewNotifications[0];
          showBrowserNotification(
            latest.title || 'Neue Benachrichtigung',
            latest.message || 'Sie haben eine neue Nachricht',
            latest.icon
          );
        }

        // Update state
        setNotifications(newNotifications);
        setUnreadCount(newNotifications.filter(n => !n.read).length);
        previousNotificationIds.current = newIds;
        setLastCheckTime(Date.now());
      }
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungen:', error);
    }
  }, [salespersonId, token, API_URL, playSound, showBrowserNotification, lastCheckTime]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!salespersonId) return;

    // Initial fetch
    fetchNotifications();

    // Set up polling interval
    const pollInterval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(pollInterval);
  }, [salespersonId, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/sales/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Fehler beim Markieren:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/sales/notifications/mark-all-read?salesperson_id=${salespersonId}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Fehler beim Markieren aller:', error);
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'tour': return <MapPin className="w-4 h-4" />;
      case 'appointment': return <Calendar className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffMins < 1440) return `vor ${Math.floor(diffMins / 60)} Std.`;
    return date.toLocaleDateString('de-DE');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!permissionGranted) {
            requestPermission();
          }
        }}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="sales-notification-bell"
      >
        <Bell className="w-6 h-6 text-white" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-green to-green-600 px-4 py-3 flex items-center justify-between">
            <h3 className="text-white font-semibold">Benachrichtigungen</h3>
            <div className="flex items-center gap-2">
              {/* Sound Toggle */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="p-1 rounded hover:bg-white/20 transition-colors"
                title={soundEnabled ? 'Ton ausschalten' : 'Ton einschalten'}
              >
                {soundEnabled ? (
                  <Volume2 className="w-4 h-4 text-white" />
                ) : (
                  <VolumeX className="w-4 h-4 text-white/60" />
                )}
              </button>
              
              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-white/20 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>Keine Benachrichtigungen</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => markAsRead(notification.id)}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-green-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      notification.read ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-600'
                    }`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        notification.read ? 'text-gray-700' : 'text-gray-900'
                      }`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTime(notification.timestamp)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && unreadCount > 0 && (
            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={markAllAsRead}
                className="w-full text-center text-sm text-brand-green hover:text-green-700 font-medium flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Alle als gelesen markieren
              </button>
            </div>
          )}

          {/* Permission Request */}
          {!permissionGranted && (
            <div className="p-3 bg-yellow-50 border-t border-yellow-100">
              <button
                onClick={requestPermission}
                className="w-full text-center text-sm text-yellow-700 font-medium"
              >
                🔔 Desktop-Benachrichtigungen aktivieren
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesNotificationSystem;
